import { withAssetCacheVersion, cdnFallbackUrl } from '../utils/assetCache';

const PROVIDER_LABELS = {
  pragmatic: 'Pragmatic Play',
  pgsoft: 'PG Soft',
  amatic: 'Amatic',
  amusnet: 'Amusnet',
  egtdigital: 'EGT Digital',
  drakon: 'Drakon',
};

const PLACEHOLDER_PATH = '/uploads/game-icons/_placeholder.webp';
export const GAME_ICON_THUMB_MD = 240;
export const GAME_ICON_THUMB_SM = 120;
const GAME_ICON_THUMB_WIDTH = GAME_ICON_THUMB_MD;
const failedGameIconUrls = new Set();
const loadedGameIconUrls = new Set();
const warmingGameIconUrls = new Set();

function iconUrlKey(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    if (/^https?:\/\//i.test(url)) {
      const u = new URL(url);
      return `${u.origin}${u.pathname}`;
    }
  } catch {
  }
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
}

function pathnameOf(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    if (/^https?:\/\//i.test(url)) return new URL(url).pathname;
  } catch {
  }
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
}

function thumbWidthForSize(size) {
  return size === 'sm' ? GAME_ICON_THUMB_SM : GAME_ICON_THUMB_MD;
}

export function gameIconThumbPath(url, width = GAME_ICON_THUMB_WIDTH) {
  const pathname = pathnameOf(url);
  if (!pathname) return null;
  const thumbHit = pathname.match(/^\/uploads\/game-icon-thumbs\/t(\d+)\/([^/]+\.webp)$/i);
  if (thumbHit) {
    const file = thumbHit[2];
    if (Number(thumbHit[1]) === width) return `/uploads/game-icon-thumbs/t${width}/${file}`;
    return `/uploads/game-icon-thumbs/t${width}/${file}`;
  }
  const match = pathname.match(/^\/uploads\/game-icons\/(?!t\d+\/)([^/]+\.(?:webp|png|jpe?g))$/i);
  if (!match) return null;
  const file = match[1].replace(/\.(png|jpe?g)$/i, '.webp');
  return `/uploads/game-icon-thumbs/t${width}/${file}`;
}

export function markGameIconFailed(url) {
  const key = iconUrlKey(url);
  if (!key || key.endsWith('/_placeholder.webp')) return;
  failedGameIconUrls.add(key);
  warmingGameIconUrls.delete(key);
}

export function isGameIconFailed(url) {
  const key = iconUrlKey(url);
  return Boolean(key) && failedGameIconUrls.has(key);
}

export function markGameIconLoaded(url) {
  const key = iconUrlKey(url);
  if (!key) return;
  loadedGameIconUrls.add(key);
  warmingGameIconUrls.delete(key);
}

export function isGameIconLoaded(url) {
  const key = iconUrlKey(url);
  return Boolean(key) && loadedGameIconUrls.has(key);
}

export function gameIconPlaceholder() {
  return withAssetCacheVersion(PLACEHOLDER_PATH);
}

export function isPlaceholderUrl(url) {
  return iconUrlKey(url).endsWith('/_placeholder.webp');
}

export function optimizeGameIconUrl(url, size = 'md') {
  if (!url || typeof url !== 'string') return url;
  if (isPlaceholderUrl(url)) return withAssetCacheVersion(PLACEHOLDER_PATH);
  const pathOnly = pathnameOf(url);
  // Use pre-generated thumbs only when URL already points at them.
  if (pathOnly.startsWith('/uploads/game-icon-thumbs/')) {
    return withAssetCacheVersion(url);
  }
  return withAssetCacheVersion(url);
}

function isLocalUploadIcon(url) {
  const path = pathnameOf(url);
  return path.startsWith('/uploads/game-icons/')
    || path.startsWith('/uploads/game-icon-thumbs/');
}

function isRemirrorIconUrl(url) {
  const raw = String(url || '');
  return /[?&]v=(sspm1|softswiss2|pmby\d*)/i.test(raw) || raw.includes('sspm1') || raw.includes('softswiss2');
}

function pushLocalIconVariants(push, source, width, { thumbsFirst = true, skipSm = false } = {}) {
  if (!source || isGameIconFailed(source)) return;
  const remirror = isRemirrorIconUrl(source);
  const thumb = remirror ? null : gameIconThumbPath(source, width);
  const md = (!remirror && width !== GAME_ICON_THUMB_MD) ? gameIconThumbPath(source, GAME_ICON_THUMB_MD) : null;

  if (thumbsFirst && !remirror) {
    if (thumb) {
      const thumbFallback = cdnFallbackUrl(thumb);
      if (thumbFallback) push(thumbFallback);
      push(thumb);
    }
    if (md) {
      const mdFallback = cdnFallbackUrl(md);
      if (mdFallback) push(mdFallback);
      push(md);
    }
    const fullFallback = cdnFallbackUrl(source);
    if (fullFallback) push(fullFallback);
    push(source);
    return;
  }

  const fullFallback = cdnFallbackUrl(source);
  if (fullFallback) push(fullFallback);
  push(source);
  if (!remirror && md) {
    const mdFallback = cdnFallbackUrl(md);
    if (mdFallback) push(mdFallback);
    push(md);
  }
  if (!remirror && !skipSm && thumb && width === GAME_ICON_THUMB_SM) {
    const thumbFallback = cdnFallbackUrl(thumb);
    if (thumbFallback) push(thumbFallback);
    push(thumb);
  }
}

export function sideGameImageCandidates(game, { size = 'md', preferLandscape = false } = {}) {
  // Never fall back to _placeholder.webp — callers show a spinner until a real icon loads.
  if (!game?.id) return [];
  const mapped = [];
  const seen = new Set();
  const width = thumbWidthForSize(size);

  const push = (raw) => {
    if (!raw || isPlaceholderUrl(raw)) return;
    const next = withAssetCacheVersion(raw);
    const key = iconUrlKey(next);
    if (!key || seen.has(key) || isPlaceholderUrl(next)) return;
    if (isGameIconFailed(next)) return;
    seen.add(key);
    mapped.push(next);
  };

  const source = (!isPlaceholderUrl(game.icon_url) && game.icon_url)
    || (!isPlaceholderUrl(game.img) && game.img)
    || null;
  const fallback = (!isPlaceholderUrl(game.icon_fallback) && game.icon_fallback)
    || (!isPlaceholderUrl(game.iconFallback) && game.iconFallback)
    || null;
  const landscape = game.icon_landscape || game.iconLandscape || null;
  const usableLandscape = landscape
    && !isPlaceholderUrl(landscape)
    && !isGameIconFailed(landscape)
    ? landscape
    : null;

  if (preferLandscape) {
    // True landscape art first so 1.5:1 Digitech tiles fill edge-to-edge without soft upscales.
    if (usableLandscape && isLocalUploadIcon(usableLandscape)) {
      pushLocalIconVariants(push, usableLandscape, Math.max(width, GAME_ICON_THUMB_MD), {
        thumbsFirst: false,
        skipSm: true,
      });
    }
    if (usableLandscape && !isLocalUploadIcon(usableLandscape)) {
      push(usableLandscape);
    }
    if (source && isLocalUploadIcon(source)) {
      pushLocalIconVariants(push, source, Math.max(width, GAME_ICON_THUMB_MD), {
        thumbsFirst: false,
        skipSm: true,
      });
    } else if (source && /^https?:\/\//i.test(String(source)) && !isGameIconFailed(source)) {
      push(source);
    }
  } else if (source) {
    if (isLocalUploadIcon(source)) {
      pushLocalIconVariants(push, source, width, { thumbsFirst: false });
    } else {
      // SoftSwiss CDN first.
      push(source);
      if (fallback && isLocalUploadIcon(fallback)) {
        pushLocalIconVariants(push, fallback, width, { thumbsFirst: false });
      } else if (fallback) {
        push(fallback);
      }
    }
  }

  return mapped;
}

export function resolveGameIcon(game, size = 'md', { preferLandscape = false } = {}) {
  return sideGameImageCandidates(game, { size, preferLandscape })[0] || null;
}

export function warmGameIcon(url) {
  if (typeof window === 'undefined' || !url) return;
  const key = iconUrlKey(url);
  if (!key || isPlaceholderUrl(url) || isGameIconLoaded(url) || isGameIconFailed(url)) return;
  if (warmingGameIconUrls.has(key)) return;
  warmingGameIconUrls.add(key);
  const img = new Image();
  img.decoding = 'async';
  img.onload = () => markGameIconLoaded(url);
  img.onerror = () => markGameIconFailed(url);
  img.src = url;
}

export function mapCasinoApiGame(game) {
  const aggregatorLabel = game.aggregator === 'drakon' ? 'Drakon' : null;
  return {
    id: game.id,
    name: game.name,
    provider: game.vendor || PROVIDER_LABELS[game.provider] || aggregatorLabel || game.provider,
    providerId: game.provider,
    vendor: game.vendor,
    aggregator: game.aggregator || 'gamblehub',
    providerGameId: game.provider_game_id,
    img: resolveGameIcon(game, 'md'),
    icon_url: game.icon_url,
    icon_landscape: game.icon_landscape || game.iconLandscape || null,
    badge: game.badge ?? null,
    freespin: Boolean(game.freespin),
    demoSupported: game.demo_supported !== false
      && game.demoSupported !== false
      && !(Array.isArray(game.categories) && game.categories.includes('live')),
    symbol: game.symbol,
    minBet: game.min_bet,
    categories: Array.isArray(game.categories) ? game.categories : [],
    popularity: game.popularity_score ?? 0,
  };
}


export function isImmersiveRouletteDeluxeDesktop(name) {
  return /^immersive\s*roulette\s*deluxe$/i.test(String(name || '').trim());
}

export function isImmersiveRouletteDeluxeMobile(name) {
  return /^immersive\s*roulette\s*deluxe\s*mobile$/i.test(String(name || '').trim());
}

/** Desktop: Deluxe only. Mobile: Deluxe Mobile only. */
export function filterImmersiveDeluxeByDevice(games, isMobile) {
  if (!Array.isArray(games) || !games.length) return Array.isArray(games) ? games : [];
  return games.filter((g) => {
    const name = g?.name || '';
    if (isImmersiveRouletteDeluxeMobile(name)) return Boolean(isMobile);
    if (isImmersiveRouletteDeluxeDesktop(name)) return !isMobile;
    return true;
  });
}

export function gameSupportsDemo(game) {
  if (!game) return false;
  if (game.demoSupported === false || game.demo_supported === false) return false;
  if (Array.isArray(game.categories) && game.categories.includes('live')) return false;
  return true;
}

export function gamesForLobby(games, lobbyId) {
  if (lobbyId === 'slots' || lobbyId == null) return games;
  return [];
}
